import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNotifications } from '../contexts/NotificationContext';
import { 
  Container, 
  Typography, 
  Box, 
  Paper, 
  Avatar, 
  Button, 
  TextField, 
  Grid,
  CircularProgress,
  Divider,
  Card,
  CardContent,
  FormControlLabel,
  Switch,
  Tabs,
  Tab,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip
} from '@mui/material';
import { getStorage, ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { updateUserProfile } from '../firebase/services/userService';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import NotificationsIcon from '@mui/icons-material/Notifications';
import SecurityIcon from '@mui/icons-material/Security';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';

// Composant pour afficher une date formatée
const FormattedDate = ({ date, defaultText = "Non disponible" }) => {
  if (!date) return <Typography variant="body2" color="text.secondary">{defaultText}</Typography>;
  
  // Si la date est un timestamp Firestore
  if (date && typeof date.toDate === 'function') {
    date = date.toDate();
  }
  
  return (
    <Typography variant="body2">
      {format(new Date(date), 'PPP à HH:mm', { locale: fr })}
    </Typography>
  );
};

const ProfilePage = () => {
  const { currentUser, refreshUserProfile } = useAuth();
  const { addSystemNotification } = useNotifications();
  const [displayName, setDisplayName] = useState('');
  const [photoURL, setPhotoURL] = useState('');
  const [loading, setLoading] = useState(false);
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [tabValue] = useState(0);
  const [userSettings, setUserSettings] = useState({
    theme: 'light',
    notifications: true
  });
  
  // Effet pour initialiser le composant avec les données de l'utilisateur
  useEffect(() => {
    // Force le rafraîchissement du profil au chargement
    const initProfile = async () => {
      if (currentUser) {
        try {
          await refreshUserProfile();
              } catch (error) {
        // Erreur lors du rafraîchissement initial du profil
      }
      }
    };
    
    initProfile();
  }, []);
  
  // Effet pour mettre à jour les données du formulaire lorsque currentUser change
  useEffect(() => {
    if (currentUser) {
      // Déterminer quelle valeur utiliser pour le displayName
      let nameToUse = '';
      
      if (currentUser.profile && currentUser.profile.displayName) {
        nameToUse = currentUser.profile.displayName;
      } else if (currentUser.displayName) {
        nameToUse = currentUser.displayName;
      } else if (currentUser.email) {
        nameToUse = currentUser.email.split('@')[0];
      }
      
      // Mettre à jour l'état du formulaire
      setDisplayName(nameToUse);
      setPhotoURL(currentUser.profile?.photoURL || currentUser.photoURL || '');
      
      // Initialiser les settings
      if (currentUser.profile?.settings) {
        setUserSettings({
          theme: currentUser.profile.settings.theme || 'light',
          notifications: currentUser.profile.settings.notifications !== false
        });
      }
    }
  }, [currentUser]);

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      
      // Vérifier le type de fichier
      if (!file.type.match('image.*')) {
        addSystemNotification('Veuillez sélectionner une image (JPG, PNG, GIF)', 'error');
        return;
      }
      
      // Vérifier la taille du fichier (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        addSystemNotification("La taille de l'image ne doit pas dépasser 5 Mo", 'error');
        return;
      }
      
      setPhotoFile(file);
      
      // Créer un aperçu de l'image
      const reader = new FileReader();
      reader.onload = (e) => {
        setPhotoPreview(e.target.result);
      };
      reader.readAsDataURL(file);
    }
  };
  
  const uploadPhoto = async () => {
    if (!photoFile) return null;
    
    try {
      const storage = getStorage();
      const fileExtension = photoFile.name.split('.').pop();
      const fileName = `profile_photos/${currentUser.uid}_${Date.now()}.${fileExtension}`;
      const storageRef = ref(storage, fileName);
      
      await uploadBytes(storageRef, photoFile);
      const downloadURL = await getDownloadURL(storageRef);
      
      return downloadURL;
    } catch (error) {
      throw error;
    }
  };
  
  const handleSettingsChange = (setting, value) => {
    setUserSettings(prev => ({
      ...prev,
      [setting]: value
    }));
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      // Initialiser les données à mettre à jour
      const updateData = {
        displayName: displayName,
        settings: userSettings
      };
      
      // Si une nouvelle photo a été sélectionnée, la télécharger
      if (photoFile) {
        const newPhotoURL = await uploadPhoto();
        if (newPhotoURL) {
          updateData.photoURL = newPhotoURL;
          setPhotoURL(newPhotoURL);
        }
      }
      
      // Traitement spécial pour les utilisateurs Azure
      if (currentUser.profile && currentUser.profile.isAzureUser) {
        // Assurer que le displayName est enregistré au bon endroit pour les utilisateurs Azure
        updateData.displayName = displayName;
        
        // Si l'utilisateur a un azureId, s'assurer que la mise à jour le préserve
        if (currentUser.profile.azureId) {
          updateData.azureId = currentUser.profile.azureId;
        }
      }
      
      // Mettre à jour le profil dans Firestore en utilisant la fonction dédiée
      await updateUserProfile(currentUser.uid, updateData);
      
      // Rafraîchir le profil utilisateur dans le contexte
      if (refreshUserProfile) {
        await refreshUserProfile();
      }
      
      addSystemNotification('Profil mis à jour avec succès', 'success');
      
      // Réinitialiser l'état du fichier
      setPhotoFile(null);
      setPhotoPreview(null);
    } catch (error) {
      addSystemNotification('Erreur lors de la mise à jour du profil: ' + error.message, 'error');
    } finally {
      setLoading(false);
    }
  };
  
  if (!currentUser) {
    return (
      <Container maxWidth="md">
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
          <CircularProgress />
          <Typography sx={{ ml: 2 }}>Chargement du profil...</Typography>
        </Box>
      </Container>
    );
  }
  
  return (
    <Container maxWidth="md">
      <Paper elevation={3} sx={{ p: 4, mt: 4, mb: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom align="center">
          Mon Profil
        </Typography>
        
        <Divider sx={{ mb: 4 }} />
        
        <Box sx={{ mb: 4 }}>
          <Tabs 
            value={0} 
            variant="fullWidth" 
            indicatorColor="primary"
            textColor="primary"
            aria-label="sections du profil"
          >
            <Tab icon={<AccountCircleIcon />} label="Informations" />
          </Tabs>
        </Box>
        
        <Box component="form" onSubmit={handleSubmit}>
          <Grid container spacing={4}>
            <Grid item xs={12} md={4} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <Box sx={{ position: 'relative', mb: 2 }}>
                <Avatar
                  src={photoPreview || photoURL}
                  alt={displayName || 'Photo de profil'}
                  sx={{ width: 150, height: 150, mb: 2 }}
                />
                <Button
                  component="label"
                  variant="contained"
                  sx={{
                    position: 'absolute',
                    bottom: 10,
                    right: 0,
                    borderRadius: '50%',
                    minWidth: 0,
                    width: 40,
                    height: 40,
                    p: 0
                  }}
                >
                  <CameraAltIcon />
                  <input
                    type="file"
                    hidden
                    accept="image/*"
                    onChange={handleFileChange}
                  />
                </Button>
              </Box>
              <Typography variant="body2" color="textSecondary" align="center" sx={{ mt: 1 }}>
                Cliquez sur l'icône pour modifier votre photo
              </Typography>
              
              <Card sx={{ width: '100%', mt: 4 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Informations du compte
                  </Typography>
                  <Box sx={{ mb: 1.5 }}>
                    <Typography variant="body2" color="text.secondary">
                      Identifiant:
                    </Typography>
                    <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>
                      {currentUser.uid}
                    </Typography>
                  </Box>
                  <Box sx={{ mb: 1.5 }}>
                    <Typography variant="body2" color="text.secondary">
                      Créé le:
                    </Typography>
                    <FormattedDate date={currentUser.profile?.createdAt} />
                  </Box>
                  <Box sx={{ mb: 1.5 }}>
                    <Typography variant="body2" color="text.secondary">
                      Dernière connexion:
                    </Typography>
                    <FormattedDate date={currentUser.profile?.lastLogin} />
                  </Box>
                  <Box sx={{ mb: 1.5 }}>
                    <Typography variant="body2" color="text.secondary">
                      Dernière mise à jour:
                    </Typography>
                    <FormattedDate date={currentUser.profile?.updatedAt} />
                  </Box>
                  {currentUser.profile && (
                    <Box sx={{ mt: 2 }}>
                      {currentUser.profile.isAdmin && (
                        <Chip 
                          label="Administrateur" 
                          color="primary" 
                          size="small" 
                          sx={{ mr: 1, mb: 1 }} 
                        />
                      )}
                      {currentUser.profile.isAzureUser && (
                        <Chip 
                          label="Utilisateur Azure" 
                          color="info" 
                          size="small" 
                          sx={{ mr: 1, mb: 1 }} 
                        />
                      )}
                      {currentUser.profile.role && (
                        <Chip 
                          label={`Rôle: ${currentUser.profile.role}`} 
                          color="default" 
                          size="small" 
                          sx={{ mr: 1, mb: 1 }} 
                        />
                      )}
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Grid>
            
            <Grid item xs={12} md={8}>
              <TextField
                fullWidth
                label="Nom d'affichage"
                variant="outlined"
                value={displayName}
                onChange={(e) => {
                  setDisplayName(e.target.value);
                }}
                margin="normal"
                required
                InputLabelProps={{ shrink: true }}
              />
              
              <TextField
                fullWidth
                label="Email"
                variant="outlined"
                value={currentUser.email}
                margin="normal"
                disabled
                InputLabelProps={{ shrink: true }}
              />
              
              <Box sx={{ mt: 4, display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  disabled={loading}
                >
                  {loading ? <CircularProgress size={24} /> : 'Enregistrer les modifications'}
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Box>
      </Paper>
      
    </Container>
  );
};

export default ProfilePage; 